import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Trash2, Save, Upload, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const imgUrl = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  return `${BACKEND_URL}${src}`;
};

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const blankProduct = () => ({
  id: "",
  slug: "",
  name: "",
  tagline: "",
  description: "",
  price_usd: 70,
  shirt_color: "Cream",
  color_chip: "#FFFDF5",
  status: "in stock",
  images: [],
  features: [],
  colors: [],
  variants: SIZES.map((s) => ({ size: s, in_stock: true })),
});

const ProductsPanel = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {product, isNew}
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data || []);
    } catch (e) {
      toast.error("Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (p) => setEditing({ product: { ...p }, isNew: false });
  const startNew = () => setEditing({ product: blankProduct(), isNew: true });

  const onSave = async (data, isNew) => {
    try {
      if (isNew) {
        await axios.post(`${API}/admin/products`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product created.");
      } else {
        await axios.put(`${API}/admin/products/${data.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product saved.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product deleted.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid="admin-products-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70">
          {products.length} product{products.length === 1 ? "" : "s"} · stored in MongoDB
        </p>
        <button
          onClick={startNew}
          className="btn-duds-sm flex items-center gap-1"
          data-testid="admin-new-product"
        >
          <Plus size={14} /> New product
        </button>
      </div>

      <div className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard overflow-x-auto" data-testid="admin-products-table">
        <table className="w-full text-sm">
          <thead className="bg-[#1C1914] text-[#FFFDF5] font-condensed uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-4 py-3 w-16"></th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Slug</th>
              <th className="text-left px-4 py-3">Price</th>
              <th className="text-left px-4 py-3">Colour</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="font-body">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#1C1914]/60">Loading…</td></tr>
            )}
            {!loading && products.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[#1C1914]/60">
                  No products. Click "New product" to add the first one.
                </td>
              </tr>
            )}
            {products.map((p) => (
              <tr
                key={p.id}
                className="border-t border-[#1C1914]/15 hover:bg-[#FDE047]/20 cursor-pointer"
                onClick={() => startEdit(p)}
                data-testid={`admin-product-row-${p.id}`}
              >
                <td className="px-4 py-3">
                  <img
                    src={imgUrl(p.images?.[0])}
                    alt={p.name}
                    className="w-12 h-12 object-cover border-2 border-[#1C1914]"
                    onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                  />
                </td>
                <td className="px-4 py-3 font-display text-base uppercase">{p.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.slug}</td>
                <td className="px-4 py-3 font-display">A${p.price_usd?.toFixed(2)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className="inline-block w-4 h-4 border-2 border-[#1C1914] align-middle mr-2"
                    style={{ background: p.color_chip || "#fff" }}
                  />
                  <span className="text-xs">{p.shirt_color}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-1 bg-[#1C1914]/10 font-condensed text-xs uppercase tracking-widest">
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(p); }}
                    className="text-[#1C1914]/60 hover:text-[#D95D39]"
                    aria-label="Delete"
                    data-testid={`admin-product-delete-${p.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductEditDrawer
          token={token}
          initial={editing.product}
          isNew={editing.isNew}
          onSave={onSave}
          onClose={() => setEditing(null)}
          onDelete={() => onDelete(editing.product)}
        />
      )}
    </div>
  );
};

const ProductEditDrawer = ({ token, initial, isNew, onSave, onClose, onDelete }) => {
  const [p, setP] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const set = (k, v) => setP((cur) => ({ ...cur, [k]: v }));

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of fileList) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await axios.post(`${API}/admin/products/upload-image`, form, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data.url);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    if (uploaded.length > 0) {
      setP((cur) => ({ ...cur, images: [...(cur.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded.`);
    }
    setUploading(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    await uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const updateImage = (idx, value) => {
    const next = [...(p.images || [])];
    next[idx] = value;
    set("images", next);
  };
  const removeImage = (idx) => set("images", (p.images || []).filter((_, i) => i !== idx));

  const updateFeature = (idx, value) => {
    const next = [...(p.features || [])];
    next[idx] = value;
    set("features", next);
  };
  const addFeature = () => set("features", [...(p.features || []), ""]);
  const removeFeature = (idx) => set("features", (p.features || []).filter((_, i) => i !== idx));

  const toggleSize = (size) => {
    const cur = p.variants || [];
    const idx = cur.findIndex((v) => v.size === size);
    if (idx >= 0) {
      const next = [...cur];
      next[idx] = { ...next[idx], in_stock: !next[idx].in_stock };
      set("variants", next);
    } else {
      set("variants", [...cur, { size, in_stock: true }]);
    }
  };

  const save = () => {
    // Validate colours BEFORE saving so the user knows what went wrong
    // instead of seeing a "Product saved" toast that silently drops rows.
    const rawColors = p.colors || [];
    const invalid = rawColors
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => !c || !c.name?.trim() || !c.chip);
    if (invalid.length > 0) {
      const positions = invalid.map(({ i }) => `#${i + 1}`).join(", ");
      toast.error(
        `Fill the name (and colour chip) on each colour row before saving — missing on ${positions}.`,
      );
      return;
    }
    // A single colour row is allowed (e.g. you're renaming the base colour),
    // but warn the user that the swatcher only renders on the PDP when >= 2.
    if (rawColors.length === 1) {
      toast.warning(
        "Heads up — only 1 colour set, so no colour swatcher will show on the product page. Add a 2nd colour to enable colour selection.",
      );
    }

    const payload = {
      ...p,
      images: (p.images || []).filter(Boolean),
      features: (p.features || []).filter(Boolean),
      colors: rawColors.map((c) => ({
        name: c.name.trim(),
        chip: c.chip || "#FFFDF5",
        images: (c.images || []).filter(Boolean),
      })),
      price_usd: parseFloat(p.price_usd) || 0,
    };
    if (!payload.slug || !payload.name) {
      toast.error("Name and slug are required.");
      return;
    }
    onSave(payload, isNew);
  };

  return (
    <>
      <div className="fixed inset-0 bg-[#1C1914]/60 z-50" onClick={onClose} data-testid="product-edit-backdrop" />
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[600px] bg-[#FFFDF5] z-50 border-l-4 border-[#1C1914] overflow-y-auto"
        data-testid="product-edit-drawer"
      >
        <div className="sticky top-0 bg-[#FFFDF5] flex items-center justify-between px-5 py-4 border-b-4 border-[#1C1914] z-10">
          <div>
            <p className="font-condensed text-xs uppercase tracking-widest text-[#D95D39]">
              // {isNew ? "New product" : "Edit product"}
            </p>
            <h2 className="font-display text-2xl uppercase leading-none mt-1">
              {p.name || "Untitled"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="border-2 border-[#1C1914] p-2 hover:bg-[#FDE047]"
            data-testid="product-edit-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">
          <Field label="Name">
            <input
              className="input-duds"
              value={p.name || ""}
              onChange={(e) => set("name", e.target.value)}
              data-testid="product-field-name"
            />
          </Field>
          <Field label="Slug (URL)" hint="lowercase-with-hyphens. Used in /product/[slug]">
            <input
              className="input-duds font-mono"
              value={p.slug || ""}
              onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))}
              data-testid="product-field-slug"
            />
          </Field>
          <Field label="Tagline">
            <input
              className="input-duds"
              value={p.tagline || ""}
              onChange={(e) => set("tagline", e.target.value)}
              data-testid="product-field-tagline"
            />
          </Field>
          <Field label="Description">
            <textarea
              className="input-duds min-h-[100px]"
              value={p.description || ""}
              onChange={(e) => set("description", e.target.value)}
              data-testid="product-field-description"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (AUD)">
              <input
                type="number"
                step="0.01"
                className="input-duds"
                value={p.price_usd}
                onChange={(e) => set("price_usd", e.target.value)}
                data-testid="product-field-price"
              />
            </Field>
            <Field label="Status">
              <select
                className="input-duds"
                value={p.status || "in stock"}
                onChange={(e) => set("status", e.target.value)}
                data-testid="product-field-status"
              >
                <option value="in stock">in stock</option>
                <option value="new drop">new drop</option>
                <option value="dropping soon">dropping soon</option>
                <option value="sold out">sold out</option>
                <option value="archived">archived</option>
              </select>
            </Field>
            <Field label="Shirt colour name">
              <input
                className="input-duds"
                value={p.shirt_color || ""}
                onChange={(e) => set("shirt_color", e.target.value)}
                data-testid="product-field-shirt-color"
              />
            </Field>
            <Field label="Colour chip (hex)">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  className="w-12 h-10 border-2 border-[#1C1914]"
                  value={p.color_chip || "#FFFDF5"}
                  onChange={(e) => set("color_chip", e.target.value)}
                  data-testid="product-field-color-chip"
                />
                <input
                  className="input-duds flex-1 font-mono"
                  value={p.color_chip || ""}
                  onChange={(e) => set("color_chip", e.target.value)}
                />
              </div>
            </Field>
          </div>

          {/* Images */}
          <Field label="Images" hint="Drop PNG/JPG/WEBP files here or paste a URL. First image is the cover.">
            <div className="space-y-2">
              {(p.images || []).map((img, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`product-image-row-${i}`}>
                  {img ? (
                    <img
                      src={imgUrl(img)}
                      alt=""
                      className="w-12 h-12 object-cover border-2 border-[#1C1914] flex-shrink-0"
                      onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
                    />
                  ) : (
                    <div className="w-12 h-12 border-2 border-dashed border-[#1C1914]/40 flex-shrink-0 bg-[#1C1914]/5" />
                  )}
                  <input
                    className="input-duds flex-1 font-mono text-xs"
                    value={img}
                    onChange={(e) => updateImage(i, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="text-[#1C1914]/60 hover:text-[#D95D39] p-2"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              {/* Drag-drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed px-4 py-6 cursor-pointer transition-colors text-center ${
                  dragOver ? "border-[#D95D39] bg-[#FDE047]/40" : "border-[#1C1914] bg-[#1C1914]/5 hover:bg-[#FDE047]/30"
                }`}
                data-testid="product-image-dropzone"
              >
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 font-condensed text-sm uppercase tracking-widest">
                    <Loader2 size={16} className="animate-spin" /> Uploading…
                  </div>
                ) : (
                  <>
                    <Upload size={20} className="mx-auto mb-2 text-[#1C1914]/60" />
                    <p className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]">
                      Drag images here
                    </p>
                    <p className="font-body text-xs text-[#1C1914]/60 mt-1">
                      or click to choose · PNG/JPG/WEBP · 15 MB max each
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
                  data-testid="product-image-file-input"
                />
              </div>

              <button
                type="button"
                onClick={() => set("images", [...(p.images || []), ""])}
                className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/60 hover:text-[#1C1914]"
                data-testid="product-add-image"
              >
                <Plus size={12} className="inline" /> Add by URL instead
              </button>
            </div>
          </Field>

          {/* Features */}
          <Field label="Features">
            <div className="space-y-2">
              {(p.features || []).map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    className="input-duds flex-1"
                    value={f}
                    onChange={(e) => updateFeature(i, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeFeature(i)}
                    className="text-[#1C1914]/60 hover:text-[#D95D39] p-2"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="border-2 border-dashed border-[#1C1914] px-3 py-2 w-full font-condensed text-xs uppercase tracking-widest hover:bg-[#FDE047]/30"
                data-testid="product-add-feature"
              >
                <Plus size={14} className="inline mr-1" /> Add feature
              </button>
            </div>
          </Field>

          {/* Colours (optional — only for multi-colour products) */}
          <Field
            label="Colours (optional)"
            hint="Leave empty for single-colour products. Add 2+ to show a colour swatcher on the PDP. Each colour has its own image list."
          >
            <div className="space-y-3">
              {(p.colors || []).map((c, i) => (
                <ColorRow
                  key={i}
                  token={token}
                  color={c}
                  onChange={(next) => {
                    const arr = [...(p.colors || [])];
                    arr[i] = next;
                    set("colors", arr);
                  }}
                  onRemove={() => {
                    set("colors", (p.colors || []).filter((_, j) => j !== i));
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() => set("colors", [...(p.colors || []), { name: "", chip: "#FFFDF5", images: [] }])}
                className="border-2 border-dashed border-[#1C1914] px-3 py-2 w-full font-condensed text-xs uppercase tracking-widest hover:bg-[#FDE047]/30"
                data-testid="product-add-color"
              >
                <Plus size={14} className="inline mr-1" /> Add colour
              </button>
            </div>
          </Field>

          {/* Sizes */}
          <Field label="Sizes in stock">
            <div className="flex flex-wrap gap-2">
              {SIZES.map((size) => {
                const v = (p.variants || []).find((x) => x.size === size);
                const active = v ? v.in_stock : false;
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleSize(size)}
                    className={`px-3 py-2 border-2 border-[#1C1914] font-condensed text-xs uppercase tracking-widest ${
                      active ? "bg-[#1C1914] text-[#FFFDF5]" : "bg-[#FFFDF5] text-[#1C1914] opacity-50"
                    }`}
                    data-testid={`product-size-toggle-${size}`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="flex gap-3 pt-4 border-t-2 border-[#1C1914]">
            <button
              type="button"
              onClick={save}
              className="btn-duds flex-1 flex items-center justify-center gap-2"
              data-testid="product-save-button"
            >
              <Save size={16} /> {isNew ? "Create" : "Save"}
            </button>
            {!isNew && (
              <button
                type="button"
                onClick={onDelete}
                className="border-2 border-[#D95D39] text-[#D95D39] px-4 py-2 font-condensed uppercase tracking-widest text-sm hover:bg-[#D95D39] hover:text-[#FFFDF5]"
                data-testid="product-delete-button"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-1">
      {label}
    </label>
    {children}
    {hint && <p className="text-xs text-[#1C1914]/50 mt-1">{hint}</p>}
  </div>
);

const ColorRow = ({ token, color, onChange, onRemove }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Keep a ref to the latest `color` so async uploads finishing AFTER the user
  // has typed a name don't overwrite their input with a stale closure.
  const colorRef = useRef(color);
  colorRef.current = color;

  const setField = (k, v) => onChange({ ...colorRef.current, [k]: v });

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const urls = [];
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await axios.post(`${API}/admin/products/upload-image`, form, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        urls.push(res.data.url);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    if (urls.length > 0) {
      // Use the ref, not the closure-captured `color`, so any concurrent
      // edits (typing a name) survive the upload-completion callback.
      const latest = colorRef.current;
      onChange({ ...latest, images: [...(latest.images || []), ...urls] });
      toast.success(`${urls.length} image${urls.length === 1 ? "" : "s"} added.`);
    }
    setUploading(false);
  };

  return (
    <div className="border-2 border-[#1C1914] bg-[#FFFDF5]/60 p-3 space-y-2" data-testid={`product-color-row-${color.name || "new"}`}>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={color.chip || "#FFFDF5"}
          onChange={(e) => setField("chip", e.target.value)}
          className="w-10 h-10 border-2 border-[#1C1914] flex-shrink-0"
          aria-label="Colour chip"
        />
        <input
          className="input-duds flex-1 !py-1.5"
          placeholder="Colour name (e.g. Bone)"
          value={color.name || ""}
          onChange={(e) => setField("name", e.target.value)}
        />
        <input
          className="input-duds w-24 !py-1.5 font-mono text-xs"
          placeholder="#FFF"
          value={color.chip || ""}
          onChange={(e) => setField("chip", e.target.value)}
        />
        <button
          type="button"
          onClick={onRemove}
          className="text-[#1C1914]/60 hover:text-[#D95D39] p-2"
          aria-label="Remove colour"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {(color.images || []).map((src, i) => (
          <div key={i} className="relative">
            <img
              src={imgUrl(src)}
              alt=""
              className="w-14 h-14 object-cover border-2 border-[#1C1914]"
              onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
            />
            <button
              type="button"
              onClick={() => setField("images", color.images.filter((_, j) => j !== i))}
              className="absolute -top-2 -right-2 bg-[#FFFDF5] border-2 border-[#1C1914] p-0.5"
            >
              <X size={10} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-14 h-14 border-2 border-dashed border-[#1C1914] flex items-center justify-center hover:bg-[#FDE047]/30"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          className="hidden"
          onChange={(e) => upload(Array.from(e.target.files || []))}
        />
      </div>
    </div>
  );
};

export default ProductsPanel;
